/*
  <theme-carousel> — DUP-01
  Replaces Swiper 11 on sections/carousel.liquid and sections/shop-by-device.liquid.
  Flickity 2.3.0 comes from assets/vendor-scripts-v11.js, which every page already
  loads, so this adds no library — it removes swiper.js (152 KB) and swiper.css (18 KB).

  Swiper's class names are kept on purpose (.swiper-slide, .swiper-button-disabled,
  .swiper-button-lock, .swiper-pagination-bullet-active, .swiper-pagination-lock,
  .swiper-pagination-progressbar-fill) so no section CSS had to be rewritten.

  MUST load as a deferred EXTERNAL script from <body>. Inline would run at parse time,
  before vendor-scripts-v11.js at layout/theme.liquid:370, and Flickity would be
  undefined. See audit/shipped/2026-09-07-investigate-flickity.md §d.

  The whole file sits inside a customElements.get() guard so a second execution — two
  sections both render snippets/theme-carousel-assets.liquid — is a silent no-op. The
  class is declared inside the block, not at top level, because two identical
  <script src> tags DO both execute; a top-level `class` would throw a redeclaration
  SyntaxError on the second run.
*/

if (!customElements.get('theme-carousel')) {
  let carouselUid = 0

  class ThemeCarousel extends HTMLElement {
    connectedCallback() {
      this.cells = parseFloat(this.dataset.cells)
      if (!this.cells) return

      this.slider = this.querySelector('.swiper-wrapper')
      if (!this.slider) return

      this.gap = parseFloat(this.dataset.gap) || 0
      this.breakpoints = this.parseBreakpoints()
      this.uid = ++carouselUid
      this.controller = new AbortController()

      this.setAttribute('data-carousel-uid', this.uid)
      this.injectStyle()
      this.init()
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort()
      if (this.flickity) {
        this.flickity.destroy()
        this.flickity = null
      }
      if (this.styleEl) {
        this.styleEl.remove()
        this.styleEl = null
      }
    }

    get scope() {
      return this.closest('[data-section-id]') || this.closest('.shopify-section') || document
    }

    parseBreakpoints() {
      const raw = this.dataset.breakpoints
      if (!raw) return []

      let parsed
      try {
        parsed = JSON.parse(raw)
      } catch (error) {
        console.warn('theme-carousel: could not parse data-breakpoints', error)
        return []
      }

      return Object.keys(parsed)
        .map(Number)
        .filter(min => min > 0)
        .sort((a, b) => a - b)
        .map(min => {
          const bp = parsed[min] || {}
          return {
            min: min,
            cells: parseFloat(bp.cells) || this.cells,
            gap: bp.gap == null ? this.gap : parseFloat(bp.gap)
          }
        })
    }

    cellRule(cells, gap) {
      const gaps = +(cells - 1).toFixed(4)
      return '[data-carousel-uid="' + this.uid + '"] .swiper-slide{' +
        'width:calc((100% - ' + gap + 'px * ' + gaps + ') / ' + cells + ');' +
        'margin-right:' + gap + 'px}'
    }

    injectStyle() {
      let css = this.cellRule(this.cells, this.gap) +
        '[data-carousel-uid="' + this.uid + '"] .swiper-slide:last-child{margin-right:0}'

      this.breakpoints.forEach(bp => {
        css += '@media screen and (min-width: ' + bp.min + 'px){' + this.cellRule(bp.cells, bp.gap) + '}'
      })

      this.styleEl = document.createElement('style')
      this.styleEl.textContent = css
      this.prepend(this.styleEl)
    }

    init() {
      if (!window.Flickity) {
        if (this.waitedForLoad) {
          console.warn('theme-carousel: Flickity unavailable, leaving slides as a static row')
          return
        }
        this.waitedForLoad = true
        window.addEventListener('load', () => this.init(), { once: true, signal: this.controller.signal })
        return
      }

      this.flickity = new window.Flickity(this.slider, {
        cellAlign: 'left',
        contain: true,
        groupCells: false,
        prevNextButtons: false,
        pageDots: false,
        draggable: true,
        dragThreshold: 3,
        freeScroll: false,
        resize: true,
        accessibility: true,
        imagesLoaded: this.dataset.imagesLoaded === 'true'
      })

      this.bindNav()
      this.bindPagination()
      this.remeasureOnLoad()
      this.updateLocks()

      this.flickity.on('resize', () => this.onResize())
    }

    // Flickity rebuilds this.slides inside positionCells(), which resize() calls BEFORE
    // it emits 'resize' — so slides.length is already the new count here.
    onResize() {
      if (this.dataset.paginationType === 'bullets' &&
        this.pagination && this.flickity.slides.length !== this.bulletCount) {
        this.buildBullets()
      }
      if (this.setProgress && this.flickity.slides.length <= 1) this.setProgress(1)
      if (this.prevEl && this.nextEl) this.updateNav()
      this.updateLocks()
    }

    // One slide means the whole set fits and there is nothing to scroll. Swiper hid its
    // nav and pagination in that state; disabled-but-visible arrows are what this restores.
    updateLocks() {
      const locked = this.flickity.slides.length <= 1
      this.toggleAttribute('data-locked', locked)
      if (this.prevEl) this.prevEl.classList.toggle('swiper-button-lock', locked)
      if (this.nextEl) this.nextEl.classList.toggle('swiper-button-lock', locked)
      if (this.pagination) this.pagination.classList.toggle('swiper-pagination-lock', locked)
    }

    // vendor-scripts-v11.js was built without the imagesLoaded module, so Flickity's
    // own imagesLoaded option is inert here. Cell widths are CSS-driven and the images
    // carry width/height, so nothing should shift — this is belt-and-braces only.
    remeasureOnLoad() {
      if (this.dataset.imagesLoaded !== 'true' || window.imagesLoaded) return
      if (document.readyState === 'complete') return

      window.addEventListener('load', () => {
        if (this.flickity) this.flickity.resize()
      }, { once: true, signal: this.controller.signal })
    }

    bindNav() {
      const root = this.scope
      this.prevEl = this.dataset.navPrev ? root.querySelector(this.dataset.navPrev) : null
      this.nextEl = this.dataset.navNext ? root.querySelector(this.dataset.navNext) : null
      if (!this.prevEl || !this.nextEl) return

      const signal = this.controller.signal
      this.prevEl.addEventListener('click', () => this.flickity.previous(), { signal })
      this.nextEl.addEventListener('click', () => this.flickity.next(), { signal })

      this.updateNav()
      this.flickity.on('select', () => this.updateNav())
    }

    updateNav() {
      const last = this.flickity.slides.length - 1
      const index = this.flickity.selectedIndex
      this.prevEl.classList.toggle('swiper-button-disabled', index <= 0)
      this.nextEl.classList.toggle('swiper-button-disabled', index >= last)
    }

    bindPagination() {
      if (!this.dataset.pagination) return
      this.pagination = this.scope.querySelector(this.dataset.pagination)
      if (!this.pagination) return

      if (this.dataset.paginationType === 'bullets') this.buildBullets()
      else this.buildProgressBar()
    }

    buildProgressBar() {
      let fill = this.pagination.querySelector('.swiper-pagination-progressbar-fill')
      if (!fill) {
        fill = document.createElement('span')
        fill.className = 'swiper-pagination-progressbar-fill'
        this.pagination.appendChild(fill)
      }

      this.setProgress = value => {
        const clamped = Math.max(0, Math.min(1, value))
        fill.style.transform = 'scaleX(' + clamped + ')'
      }

      this.setProgress(this.flickity.slides.length > 1 ? 0 : 1)
      this.flickity.on('scroll', progress => this.setProgress(progress))
    }

    // Re-entrant: onResize() calls this again when a breakpoint changes slides.length.
    buildBullets() {
      const count = this.flickity.slides.length
      const signal = this.controller.signal
      const bullets = []

      if (this.bulletUpdate) this.flickity.off('select', this.bulletUpdate)
      this.bulletCount = count
      this.pagination.textContent = ''

      for (let i = 0; i < count; i++) {
        const bullet = document.createElement('button')
        bullet.type = 'button'
        bullet.className = 'swiper-pagination-bullet'
        bullet.setAttribute('aria-label', 'Go to slide ' + (i + 1))
        bullet.addEventListener('click', () => this.flickity.select(i), { signal })
        this.pagination.appendChild(bullet)
        bullets.push(bullet)
      }

      this.bulletUpdate = () => {
        const index = this.flickity.selectedIndex
        bullets.forEach((bullet, i) => {
          bullet.classList.toggle('swiper-pagination-bullet-active', i === index)
          if (i === index) bullet.setAttribute('aria-current', 'true')
          else bullet.removeAttribute('aria-current')
        })
      }

      this.bulletUpdate()
      this.flickity.on('select', this.bulletUpdate)
    }
  }

  customElements.define('theme-carousel', ThemeCarousel)
}
